/**
 * #609 — cómo se LEE `quotation_items.price_source` en pantalla.
 *
 * Esto es sólo presentación. Los valores viven en `PRICE_ROW_SOURCE`
 * (`src/utils/priceProvenance.js`, gemelo byte-equivalente del carril MCP) y de
 * ahí se importan: acá no se re-declara ninguno, porque una segunda lista de
 * strings es exactamente la forma en que los carriles derivan (#508).
 *
 * No vive en `priceProvenance.js` a propósito: ese archivo está gateado byte a
 * byte contra su gemelo de `mcp-server/`, y copiar rótulos de UI al carril del
 * servidor sería meter texto de pantalla donde no hay pantalla.
 *
 * ---------------------------------------------------------------------------
 * POR QUÉ EXISTE
 * ---------------------------------------------------------------------------
 * La compuerta de #609 impide enviar una cotización con precios que nadie
 * confirmó. Sin esto, el admin **no se entera hasta que intenta enviar** y se
 * come un rechazo que nombra las líneas en un toast: una protección sin cartel.
 * Ve "no se puede enviar" y no tiene forma de saber cuál de las diez líneas es
 * el problema ni qué hacer.
 *
 * Sigue la forma que ya usa #593 para el margen ("sin definir" ≠ "a costo"): un
 * estado se nombra en la línea, y el estado que bloquea se explica arriba con
 * un ack explícito que lo convierte en decisión.
 *
 * ---------------------------------------------------------------------------
 * NULL NO ES UN PROBLEMA
 * ---------------------------------------------------------------------------
 * `price_source = NULL` es una fila anterior a la migración `20260809060000`,
 * o una línea que el usuario acaba de agregar en el formulario y todavía no se
 * guardó. En los dos casos **no se pinta nada**: adivinar quién puso esos
 * precios sería inventar el dato que todo esto existe para no inventar. Es el
 * mismo criterio con el que la compuerta de base deja pasar los NULL.
 */

import { PRICE_ROW_SOURCE } from '../../utils/priceProvenance';

/**
 * `unverifiable` no puede estar en la columna — el CHECK de la migración sólo
 * admite `stated` / `catalog` / `unconfirmed`. Se contempla igual porque es uno
 * de los cuatro estados de `PRICE_SOURCE` (la clasificación de la *llamada*) y
 * significa lo mismo para el usuario: nadie confirmó ese número. Si algún día
 * el CHECK se abre, la pantalla ya lo lee del lado correcto.
 */
export const UNVERIFIABLE = 'unverifiable';

/** Los valores que dejan la cotización trabada. */
const BLOCKING = new Set([PRICE_ROW_SOURCE.UNCONFIRMED, UNVERIFIABLE]);

/**
 * Cómo se muestra una procedencia, o `null` si no se muestra nada.
 *
 * @param {unknown} priceSource valor crudo de `quotation_items.price_source`
 * @returns {{variant: string, icon: string, label: string, title: string, blocking: boolean}|null}
 */
export const describePriceSource = (priceSource) => {
  if (priceSource === null || priceSource === undefined || priceSource === '') {
    return null;
  }

  if (priceSource === PRICE_ROW_SOURCE.STATED) {
    return {
      variant: 'success',
      icon: 'bi-person-check',
      label: 'Precio confirmado',
      title: 'El monto lo dijo una persona: se dictó al asistente o se tipeó acá.',
      blocking: false,
    };
  }

  if (priceSource === PRICE_ROW_SOURCE.CATALOG) {
    return {
      variant: 'info',
      icon: 'bi-box-seam',
      label: 'Precio de catálogo',
      title:
        'El monto salió del precio de lista del inventario (sale_price), no de lo ' +
        'que dijo el usuario. Podés cambiarlo si el cliente acordó otro.',
      blocking: false,
    };
  }

  if (BLOCKING.has(priceSource)) {
    return {
      variant: 'warning',
      icon: 'bi-exclamation-triangle',
      label: 'Precio sin confirmar',
      title:
        'Este monto no aparece en lo que dijo el usuario ni salió del catálogo: ' +
        'lo puso el asistente. La cotización no se puede aprobar ni enviar así.',
      blocking: true,
    };
  }

  // Un valor que no conocemos NO se pinta como confirmado. Fallar al lado
  // seguro es el mismo criterio que el módulo del servidor.
  return {
    variant: 'warning',
    icon: 'bi-question-circle',
    label: 'Precio sin confirmar',
    title: `Procedencia desconocida ("${priceSource}"). Se trata como sin confirmar.`,
    blocking: true,
  };
};

/** ¿Esta línea traba la cotización? */
export const isPriceUnconfirmed = (item) => {
  const d = describePriceSource(item?.price_source);
  return d !== null && d.blocking === true;
};

/** Las líneas trabadas, en el orden en que están en pantalla. */
export const unconfirmedPriceItems = (items) =>
  (Array.isArray(items) ? items : []).filter(isPriceUnconfirmed);

/**
 * Reescribe las líneas trabadas como confirmadas. Es lo que hace el ack del
 * formulario: un humano miró los montos y los firma. Las líneas que no estaban
 * trabadas se devuelven intactas (misma referencia).
 */
export const confirmUnconfirmedPrices = (items) =>
  (Array.isArray(items) ? items : []).map((item) =>
    (isPriceUnconfirmed(item) ? { ...item, price_source: PRICE_ROW_SOURCE.STATED } : item)
  );

export default {
  describePriceSource,
  isPriceUnconfirmed,
  unconfirmedPriceItems,
  confirmUnconfirmedPrices,
};
